import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateMealPlanRequestSchema,
  type CreateMealPlanRequest,
  type MealPlanDetail,
  type MealPlanListItem,
  UpdateMealPlanRequestSchema,
  type UpdateMealPlanRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateMealPlan, useUpdateMealPlan } from '../use-meal-plans';

import { MasterCodeField } from '@/components/master-code-field';

interface MealPlanFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  mealPlan?: MealPlanDetail | MealPlanListItem | null;
}

const CREATE_DEFAULTS: CreateMealPlanRequest = {
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

const MealPlanFields = ({
  pending,
  isCreate,
}: {
  pending: boolean;
  isCreate: boolean;
}): React.JSX.Element => (
  <>
    <MasterCodeField isCreate={isCreate} required />
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Half Board"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="What's included (e.g. Breakfast + Dinner)"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive meal plans are hidden from itinerary dropdowns."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const MealPlanFormDrawer = ({
  open,
  onOpenChange,
  mode,
  mealPlan,
}: MealPlanFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMealPlan = useCreateMealPlan();
  const updateMealPlan = useUpdateMealPlan();
  const pending = createMealPlan.isPending || updateMealPlan.isPending;

  const createForm = useForm<CreateMealPlanRequest>({
    resolver: zodResolver(CreateMealPlanRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateMealPlanRequest>({
    resolver: zodResolver(UpdateMealPlanRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (mealPlan) {
      // `description` only exists on the Detail shape; ListItem also has it
      const description = (mealPlan as MealPlanDetail).description ?? '';
      updateForm.reset({
        code: mealPlan.code,
        name: mealPlan.name,
        description,
        sortOrder: mealPlan.sortOrder,
        isActive: mealPlan.isActive,
      });
    }
  }, [open, isCreate, mealPlan, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createMealPlan.mutateAsync({
      ...values,
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!mealPlan) return;
    await updateMealPlan.mutateAsync({
      id: mealPlan.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <MealPlanFields pending={pending} isCreate={isCreate} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add meal plan' : `Edit ${mealPlan?.name ?? 'meal plan'}`}
      description="The code is the stable machine identifier (e.g. HB, FB). The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
